import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertsTabs } from "@/components/AlertsTabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiClientError, api, apiWithMeta } from "../lib/api";
import { integer, money, whenever } from "../lib/format";
import i18n from "../lib/i18n";

const SELLER_KINDS = ["account_health", "seller_feedback", "order_late", "reminder"];

export interface RestockAlertRow {
  sku: string;
  asin: string | null;
  title: string | null;
  severity: "critical" | "high" | "watch";
  kind: string;
  message: string;
  fulfillable: number;
  inbound: number;
  unitsPerDay: string | null;
  daysCover: string | null;
  seasonalUplift: string | null;
  firstSeenAt: string;
  acknowledgedAt: string | null;
  acknowledgedByName: string | null;
  acknowledgedNote: string | null;
  /** When the acknowledgement lapses (null = the old fixed 7 days). */
  snoozedUntil: string | null;
  /** Open FBA inbound shipments for the SKU, one readable line each. */
  inboundDetail: string[] | null;
  totalCoverDays: string | null;
  suggestedQty: number | null;
  revenuePerDay: string | null;
  overdue: boolean;
}

/**
 * Urgent alerts (Isaac, 2026-10-12): the one place to open the app and see what
 * needs a shipment decision today. Pull, not push -- there are deliberately no
 * outbound notifications. Rows come from the daily FBA restock check
 * (server/lib/restock-alerts.ts); "Check now" runs it on demand.
 */
export default function Alerts() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["restock-alerts"],
    queryFn: () =>
      apiWithMeta<RestockAlertRow[], { checkedAt: string | null }>(
        "/api/v1/inventory/restock-alerts",
      ),
    refetchInterval: 300_000,
  });
  // Defensive: never let a malformed cache entry blank the whole page.
  const rows = Array.isArray(data?.data) ? data.data : [];
  // Biggest money problem first within each list (Isaac, 2026-10-29).
  const atRisk = (r: RestockAlertRow) => Number(r.revenuePerDay ?? 0);
  const byRisk = (a: RestockAlertRow, b: RestockAlertRow) => atRisk(b) - atRisk(a);
  const urgent = rows.filter((r) => r.severity !== "watch" && !r.acknowledgedAt);
  const quiet = rows.filter((r) => r.severity === "watch" && !r.acknowledgedAt).sort(byRisk);
  const totalAtRisk = rows
    .filter((r) => !r.acknowledgedAt && !SELLER_KINDS.includes(r.kind))
    .reduce((sum, r) => sum + atRisk(r), 0);
  const [snoozeDays, setSnoozeDays] = useState(7);
  // "Runs out by month-end": out now, or stock + inbound won't last to the last
  // day of the month. Slow sellers land here instead of being buried -- the
  // 2026-10-12 audit found 66 SKUs running out by Oct 31 with none in the main list.
  const now = new Date();
  const daysToMonthEnd =
    (new Date(now.getFullYear(), now.getMonth() + 1, 0).getTime() - now.getTime()) / 864e5;
  const monthEnd = quiet.filter(
    (r) =>
      r.fulfillable <= 0 ||
      (r.totalCoverDays !== null && Number(r.totalCoverDays) < daysToMonthEnd),
  );
  const planSoon = quiet.filter((r) => !monthEnd.includes(r));

  const runNow = useMutation({
    mutationFn: () => api("/api/v1/inventory/restock-alerts/run", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["restock-alerts"] });
      qc.invalidateQueries({ queryKey: ["restock-alert-count"] });
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["restock-alerts"] });
    qc.invalidateQueries({ queryKey: ["restock-alert-count"] });
    qc.invalidateQueries({ queryKey: ["restock-alerts-archive"] });
  };
  const acknowledge = useMutation({
    mutationFn: ({ sku, note }: { sku: string; note?: string }) =>
      api("/api/v1/inventory/restock-alerts/acknowledge", {
        method: "POST",
        body: JSON.stringify({ sku, note, snoozeDays }),
      }),
    onSuccess: refresh,
  });
  const reopen = useMutation({
    mutationFn: (sku: string) =>
      api("/api/v1/inventory/restock-alerts/reopen", {
        method: "POST",
        body: JSON.stringify({ sku }),
      }),
    onSuccess: refresh,
  });
  const onAcknowledge = (sku: string) => {
    // The note is required: with the signed-in user it is the record of who did
    // what. Cancel backs out; an empty or one-word answer asks again.
    let note: string | null = "";
    while (note !== null && note.trim().length < 3) {
      note = window.prompt(t("alerts:notePrompt"), "");
    }
    if (note === null) return;
    acknowledge.mutate({ sku, note: note.trim() });
  };

  const Row = ({ r }: { r: RestockAlertRow }) => {
    // Account-health and seller-feedback alerts reuse this list but have no stock numbers.
    const seller = SELLER_KINDS.includes(r.kind);
    return (
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1 border-t py-3 first:border-t-0">
        <div className="flex flex-col items-start gap-1">
          <Badge variant={r.severity === "critical" ? "destructive" : "warning"}>
            {t(`alerts:severity.${r.severity}`)}
          </Badge>
          {r.overdue && <Badge variant="destructive">{t("alerts:overdue")}</Badge>}
        </div>
        <div className="min-w-0 flex-1 basis-64">
          {seller ? (
            <div className="font-medium">{r.title}</div>
          ) : (
            <>
              <div>
                <span className="font-mono font-medium">{r.sku}</span>
                {r.asin && <span className="ml-2 text-muted-foreground text-xs">{r.asin}</span>}
              </div>
              {r.title && <div className="truncate text-muted-foreground text-sm">{r.title}</div>}
            </>
          )}
          <div className="mt-1 text-sm">{r.message}</div>
          {!seller && r.inboundDetail && r.inboundDetail.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-muted-foreground text-xs">
              {r.inboundDetail.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {!seller && (r.suggestedQty ?? 0) > 0 && (
            <div className="mt-1 text-sm font-medium">
              {t("alerts:suggest", { qty: integer(r.suggestedQty, locale) })}{" "}
              <a
                className="font-normal text-primary text-xs underline"
                href="https://sellercentral.amazon.com/fba/sendtoamazon"
                target="_blank"
                rel="noreferrer"
              >
                {t("alerts:createShipment")}
              </a>
            </div>
          )}
          {!seller && r.revenuePerDay && Number(r.revenuePerDay) > 0 && (
            <div className="text-muted-foreground text-xs">
              {t("alerts:revenueAtRisk", { amount: money(r.revenuePerDay, "USD", locale) })}
            </div>
          )}
          {r.acknowledgedAt && (
            <div className="mt-1 text-muted-foreground text-xs">
              {t("alerts:acknowledgedBy", {
                who: r.acknowledgedByName ?? "—",
                when: whenever(r.acknowledgedAt, locale),
              })}
              {r.acknowledgedNote ? ` — “${r.acknowledgedNote}”` : ""}
              {r.snoozedUntil
                ? ` · ${t("alerts:returns", { when: whenever(r.snoozedUntil, locale) })}`
                : ""}
            </div>
          )}
        </div>
        {!seller && (
          <dl className="grid grid-cols-5 gap-x-4 text-right text-sm tabular-nums">
            {(
              [
                ["alerts:atRisk", atRisk(r) > 0 ? money(atRisk(r).toFixed(2), "USD", locale) : "—"],
                ["alerts:inStock", r.fulfillable],
                ["alerts:inbound", r.inbound],
                ["alerts:perDay", r.unitsPerDay ? Number(r.unitsPerDay).toFixed(1) : "—"],
                ["alerts:daysLeft", r.daysCover ?? "—"],
              ] as const
            ).map(([k, v]) => (
              <div key={k}>
                <dt className="text-muted-foreground text-xs">{t(k)}</dt>
                <dd className="font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        )}
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          disabled={acknowledge.isPending || reopen.isPending}
          onClick={() => (r.acknowledgedAt ? reopen.mutate(r.sku) : onAcknowledge(r.sku))}
        >
          {r.acknowledgedAt ? t("alerts:reopen") : t("alerts:acknowledge")}
        </Button>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden />
        <h1 className="font-semibold text-2xl tracking-tight">{t("alerts:title")}</h1>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-muted-foreground text-sm">
            {data?.meta?.checkedAt
              ? t("alerts:checked", { when: whenever(data.meta.checkedAt, locale) })
              : t("alerts:neverChecked")}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={runNow.isPending}
            onClick={() => runNow.mutate()}
          >
            <RefreshCw
              className={runNow.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"}
              aria-hidden
            />
            {t("alerts:checkNow")}
          </Button>
        </div>
      </div>
      <AlertsTabs />
      <p className="mb-3 text-muted-foreground text-sm">{t("alerts:hint")}</p>
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        {totalAtRisk > 0 && (
          <span className="font-medium">
            {t("alerts:totalAtRisk", { amount: money(totalAtRisk.toFixed(2), "USD", locale) })}
          </span>
        )}
        <label className="flex items-center gap-2 text-muted-foreground">
          {t("alerts:snoozeLabel")}
          <select
            className="rounded-md border bg-background px-2 py-1 text-foreground"
            value={snoozeDays}
            onChange={(e) => setSnoozeDays(Number(e.target.value))}
          >
            {[1, 3, 7, 14].map((d) => (
              <option key={d} value={d}>
                {t("alerts:snoozeDays", { count: d })}
              </option>
            ))}
          </select>
        </label>
      </div>

      {runNow.isError && (
        <Card className="mb-4 border-destructive/50">
          <CardContent className="p-4 text-sm">
            {runNow.error instanceof ApiClientError ? runNow.error.message : t("alerts:runFailed")}
          </CardContent>
        </Card>
      )}

      <Card className="mb-5">
        <CardContent className="p-4">
          {isLoading ? (
            <p className="text-muted-foreground text-sm">{t("common:loading")}</p>
          ) : urgent.length === 0 ? (
            <p className="text-sm">{t("alerts:none")}</p>
          ) : (
            urgent.map((r) => <Row key={r.sku} r={r} />)
          )}
        </CardContent>
      </Card>

      {monthEnd.length > 0 && (
        <details className="mb-4" open>
          <summary className="tap cursor-pointer font-medium text-sm">
            {t("alerts:monthEnd", { count: monthEnd.length })}
          </summary>
          <Card className="mt-2">
            <CardContent className="p-4">
              {monthEnd.map((r) => (
                <Row key={r.sku} r={r} />
              ))}
            </CardContent>
          </Card>
        </details>
      )}

      {planSoon.length > 0 && (
        <details>
          <summary className="tap cursor-pointer text-muted-foreground text-sm">
            {t("alerts:planSoon", { count: planSoon.length })}
          </summary>
          <Card className="mt-2">
            <CardContent className="p-4">
              {planSoon.map((r) => (
                <Row key={r.sku} r={r} />
              ))}
            </CardContent>
          </Card>
        </details>
      )}
    </div>
  );
}
